// @ts-nocheck
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { Notifications } from "@/integration/application/interfaces/notification.interface";
import { IParamsInivteNotification } from "@/integration/application/interfaces/IParamsInivteNotification";
import { extractActionParams, shouldShowActions } from "../utils/types";

interface NotificationActionsProps {
  notification: Notifications;
  onAccept?: (params: IParamsInivteNotification) => void;
  onDecline?: (params: IParamsInivteNotification) => void;
  compact?: boolean;
}

/**
 * NotificationActions component
 * Displays accept/decline buttons for invitation notifications
 */
const NotificationActions: React.FC<NotificationActionsProps> = React.memo(
  ({ notification, onAccept, onDecline, compact = false }) => {
    const [isLoading, setIsLoading] = useState({
      accept: false,
      decline: false,
    });

    // Only show actions if this is an invitation that requires action
    if (!shouldShowActions(notification) || !onAccept || !onDecline) {
      return null;
    }

    const handleAction = async (
      action: (params: IParamsInivteNotification) => Promise<void>,
      isAccepting: boolean
    ) => {
      const actionType = isAccepting ? "accept" : "decline";
      setIsLoading({ ...isLoading, [actionType]: true });

      try {
        const params = extractActionParams(notification, isAccepting);
        await action(params);
      } catch (error) {
        console.error(`Error ${actionType}ing invitation:`, error);
      } finally {
        setIsLoading({ ...isLoading, [actionType]: false });
      }
    };

    return (
      <div className={`flex gap-2 ${compact ? "mt-2" : "mt-4"}`}>
        {/* Accept button */}
        <motion.button
          whileHover={{ scale: compact ? 1.02 : 1.05 }}
          whileTap={{ scale: compact ? 0.98 : 0.95 }}
          onClick={() => handleAction(onAccept, true)}
          disabled={isLoading.accept || isLoading.decline}
          className={`flex items-center gap-1 ${
            compact
              ? "px-2 py-1 rounded-lg bg-gradient-to-r from-green-600/20 to-green-700/20 text-green-400 text-xs hover:from-green-600/30 hover:to-green-700/30 transition-all duration-300 border border-green-500/30 hover:border-green-500/50"
              : "px-4 py-2 rounded-xl bg-gradient-to-r from-green-600/20 to-green-700/20 text-green-400 hover:from-green-600/30 hover:to-green-700/30 transition-all duration-300 border border-green-500/30 hover:border-green-500/50 shadow-md"
          } ${isLoading.accept ? "opacity-70" : ""}`}
          aria-busy={isLoading.accept}
        >
          <ThumbsUp size={compact ? 12 : 14} />
          <span>{isLoading.accept ? "Processando..." : "Aceitar"}</span>
        </motion.button>

        {/* Decline button */}
        <motion.button
          whileHover={{ scale: compact ? 1.02 : 1.05 }}
          whileTap={{ scale: compact ? 0.98 : 0.95 }}
          onClick={() => handleAction(onDecline, false)}
          disabled={isLoading.accept || isLoading.decline}
          className={`flex items-center gap-1 ${
            compact
              ? "px-2 py-1 rounded-lg bg-gradient-to-r from-red-600/20 to-red-700/20 text-red-400 text-xs hover:from-red-600/30 hover:to-red-700/30 transition-all duration-300 border border-red-500/30 hover:border-red-500/50"
              : "px-4 py-2 rounded-xl bg-gradient-to-r from-red-600/20 to-red-700/20 text-red-400 hover:from-red-600/30 hover:to-red-700/30 transition-all duration-300 border border-red-500/30 hover:border-red-500/50 shadow-md"
          } ${isLoading.decline ? "opacity-70" : ""}`}
          aria-busy={isLoading.decline}
        >
          <ThumbsDown size={compact ? 12 : 14} />
          <span>{isLoading.decline ? "Processando..." : "Recusar"}</span>
        </motion.button>
      </div>
    );
  }
);

NotificationActions.displayName = "NotificationActions";

export default NotificationActions;